import api from './api';

// Control de EPP / EPI: stock por depósito y entregas a colaboradores.
// Admins (SSHO, AE_EPP_Admin): catálogo, depósitos, ajustes y anulaciones.
// Responsables de depósito: stock y entregas.

export const EPP_ROL_KEY = 'epp_rol_session';

export interface DepositoRef {
  Id: number;
  Nombre: string;
}

export interface RolEpp {
  admin: boolean;
  depositos: DepositoRef[];
  tiene_entregas: boolean;
}

const ROL_VACIO: RolEpp = { admin: false, depositos: [], tiene_entregas: false };

export const esOperadorEpp = (rol: RolEpp | null) => Boolean(rol && (rol.admin || rol.depositos.length));

export type EstadoEpp =
  | 'ANULADA'
  | 'REPUESTO'
  | 'DEFINIR_DURACION'
  | 'PLAZO_CUMPLIDO'
  | 'PROXIMO_A_VENCER'
  | 'RETIRO_ANTICIPADO'
  | 'DENTRO_DEL_PLAZO';

export const ESTADOS_FILTRO: { value: EstadoEpp; label: string }[] = [
  { value: 'PLAZO_CUMPLIDO', label: 'Plazo cumplido / evaluar' },
  { value: 'PROXIMO_A_VENCER', label: 'Próximo a vencer' },
  { value: 'RETIRO_ANTICIPADO', label: 'Retiro anticipado' },
  { value: 'DENTRO_DEL_PLAZO', label: 'Dentro del plazo' },
  { value: 'DEFINIR_DURACION', label: 'Definir duración' },
  { value: 'REPUESTO', label: 'Repuesto' },
  { value: 'ANULADA', label: 'Anulada' },
];

/** Motivo de retiro anticipado del catálogo; "Otro" se escribe como texto. */
export interface MotivoAnticipada {
  Id: number;
  Nombre: string;
}

export interface Item {
  Id: number;
  Nombre: string;
  Tipo: 'EPP' | 'EPI';
  VidaUtilDias: number | null;
  Criterio: string | null;
  UsaTalle: boolean;
  Activo: boolean;
}

export type ItemPayload = Omit<Item, 'Id'>;

export interface Responsable {
  cedula: string;
  nombre: string;
}

export interface Deposito extends DepositoRef {
  Activo: boolean;
  Responsables: Responsable[];
}

export interface DepositoPayload {
  Nombre: string;
  Activo: boolean;
  Responsables: string[];
}

export interface Funcionario {
  cedula: string;
  legajo: number | null;
  nombre: string;
  cargo: string;
  departamento: string;
  unidad?: string;
}

export interface Saldo {
  ItemId: number;
  Item: string;
  Tipo: string;
  VidaUtilDias: number | null;
  Criterio: string | null;
  UsaTalle: boolean;
  Talle: string;
  Cantidad: number;
}

export interface LineaPayload {
  ItemId: number;
  Talle?: string;
  Cantidad: number;
  /** Solo retiros anticipados: un motivo del catálogo o, si es "Otro", el texto. */
  MotivoId?: number;
  MotivoOtro?: string;
}

export type TipoMovimiento = 'INGRESO' | 'TRANSFERENCIA' | 'AJUSTE' | 'ENTREGA';

export interface Movimiento {
  Fecha: string;
  Tipo: TipoMovimiento;
  ItemId: number;
  Item: string;
  Talle: string;
  Cantidad: number;
  Contraparte: string | null;
  Observacion: string | null;
  Usuario: string;
  UsuarioNombre: string;
  EntregaId: number | null;
  Anulada: boolean;
}

/** Una línea de entrega (un ítem entregado a un colaborador) con su estado. */
export interface LineaEntrega {
  Id: number;
  EntregaId: number;
  Fecha: string;
  DepositoId: number;
  Deposito: string;
  Cedula: string;
  Nombre?: string;
  Legajo?: number | null;
  Cargo?: string | null;
  Departamento?: string | null;
  EntregadoPor: string;
  EntregadoPorNombre?: string;
  ItemId: number;
  Item: string;
  Tipo: string;
  Talle: string;
  Cantidad: number;
  FechaReposicion: string | null;
  DiasRestantes: number | null;
  /** Retirado antes de la fecha estimada de la entrega anterior del ítem. */
  Anticipada: boolean;
  Motivo: string | null;
  Anulada: boolean;
  Vigente: boolean;
  AnteriorFecha: string | null;
  AnteriorReposicion: string | null;
  Estado: EstadoEpp;
  EstadoEtiqueta: string;
}

export interface EntregaDetalle {
  Id: number;
  Fecha: string;
  DepositoId: number;
  Deposito: string;
  Cedula: string;
  Nombre: string;
  Legajo: number | null;
  Cargo: string | null;
  Departamento: string | null;
  EntregadoPor: string;
  EntregadoPorNombre: string;
  Observacion: string | null;
  AnuladaPor: string | null;
  AnuladaPorNombre: string | null;
  AnuladaFecha: string | null;
  AnuladaMotivo: string | null;
  Lineas: LineaEntrega[];
}

export interface EntregaPayload {
  DepositoId: number;
  Cedula: string;
  Observacion?: string;
  Lineas: LineaPayload[];
}

/** Ítems que el backend detectó como retiro anticipado sin motivo (409). */
export interface AnticipadaSinMotivo {
  ItemId: number;
  Item: string;
  FechaAnterior: string;
  FechaReposicion: string;
}

export interface ResumenEpp {
  por_estado: Partial<Record<EstadoEpp, number>>;
  anticipadas: number;
  dias_anticipadas: number;
}

export interface FiltroEntregas {
  deposito_id?: number;
  cedula?: string;
  desde?: string;
  hasta?: string;
  estado?: EstadoEpp;
  vigentes?: 1;
  anticipadas?: 1;
}

export const fechaCorta = (valor?: string | null) => {
  if (!valor) return '—';
  const [a, m, d] = valor.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

/** "1 retiro anticipado" / "3 retiros anticipados". */
export const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

export const etiquetaItem = (item: string, talle?: string) => (talle ? `${item} (${talle})` : item);

export const mensajeError = (err: any, defecto: string) => err?.response?.data?.error || defecto;

const EppAPI = {
  async miRol(forzar = false): Promise<RolEpp> {
    if (!forzar) {
      try {
        const cache = sessionStorage.getItem(EPP_ROL_KEY);
        if (cache) return JSON.parse(cache);
      } catch {
        // sin cache
      }
    }
    try {
      const res = await api.get('epp/mi-rol');
      const rol: RolEpp = { ...ROL_VACIO, ...(res.data?.datos || {}) };
      try { sessionStorage.setItem(EPP_ROL_KEY, JSON.stringify(rol)); } catch { /* ignore */ }
      return rol;
    } catch {
      return ROL_VACIO;
    }
  },
  async resumen(): Promise<ResumenEpp> {
    return (await api.get('epp/resumen')).data?.datos;
  },
  async motivos(): Promise<MotivoAnticipada[]> {
    return (await api.get('epp/motivos')).data?.datos || [];
  },
  async buscarFuncionarios(q: string): Promise<Funcionario[]> {
    return (await api.get('epp/funcionarios', { params: { q } })).data?.datos || [];
  },

  // Catálogo y depósitos
  async items(todos = false): Promise<Item[]> {
    return (await api.get('epp/items', { params: todos ? { todos: 1 } : {} })).data?.datos || [];
  },
  crearItem(payload: ItemPayload) {
    return api.post('epp/items', payload);
  },
  actualizarItem(id: number, payload: ItemPayload) {
    return api.put(`epp/items/${id}`, payload);
  },
  async depositos(): Promise<Deposito[]> {
    return (await api.get('epp/depositos')).data?.datos || [];
  },
  async destinos(): Promise<DepositoRef[]> {
    return (await api.get('epp/depositos/destinos')).data?.datos || [];
  },
  crearDeposito(payload: DepositoPayload) {
    return api.post('epp/depositos', payload);
  },
  actualizarDeposito(id: number, payload: DepositoPayload) {
    return api.put(`epp/depositos/${id}`, payload);
  },

  // Stock
  async stock(depositoId: number): Promise<Saldo[]> {
    return (await api.get('epp/stock', { params: { deposito_id: depositoId } })).data?.datos || [];
  },
  ingreso(payload: { DepositoId: number; Referencia?: string; Lineas: LineaPayload[] }) {
    return api.post('epp/stock/ingreso', payload);
  },
  transferencia(payload: { DepositoId: number; DepositoDestinoId: number; Observacion?: string; Lineas: LineaPayload[] }) {
    return api.post('epp/stock/transferencia', payload);
  },
  ajuste(payload: { DepositoId: number; ItemId: number; Talle?: string; Cantidad: number; Motivo: string }) {
    return api.post('epp/stock/ajuste', payload);
  },
  async movimientos(depositoId: number, itemId?: number): Promise<Movimiento[]> {
    const params: any = { deposito_id: depositoId };
    if (itemId) params.item_id = itemId;
    return (await api.get('epp/stock/movimientos', { params })).data?.datos || [];
  },

  // Entregas
  async colaborador(cedula: string): Promise<{ funcionario: Funcionario; vigentes: LineaEntrega[] }> {
    return (await api.get('epp/colaborador', { params: { cedula } })).data?.datos;
  },
  registrarEntrega(payload: EntregaPayload) {
    return api.post('epp/entregas', payload);
  },
  async entregas(filtro: FiltroEntregas): Promise<LineaEntrega[]> {
    return (await api.get('epp/entregas', { params: filtro })).data?.datos || [];
  },
  async entrega(id: number): Promise<EntregaDetalle> {
    return (await api.get(`epp/entregas/${id}`)).data?.datos;
  },
  anularEntrega(id: number, motivo: string) {
    return api.post(`epp/entregas/${id}/anular`, { Motivo: motivo });
  },
  async misEpp(): Promise<LineaEntrega[]> {
    return (await api.get('epp/mis-epp')).data?.datos || [];
  },
};

export default EppAPI;
